import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './TechnicianColumnOsV2Badges.css';

function CompletedSection({ title, entries }) {
  if (!entries?.length) return null;
  return (
    <div className="tech-col-os-v2-tip-section">
      <div className="tech-col-os-v2-tip-section-title">{title}</div>
      {entries.map(([label, count]) => (
        <div key={label} className="tech-col-os-v2-tip-row">
          <span className="tech-col-os-v2-tip-row-label">{label}</span>
          <span className="tech-col-os-v2-tip-row-value">{count}</span>
        </div>
      ))}
    </div>
  );
}

export function TechnicianColumnOsV2Badges({
  openCount = 0,
  closedCount = 0,
  openStats = null,
  completedDetails = null,
  onTooltipOpenChange,
}) {
  const [open, setOpen] = useState(false);
  const [tipCoords, setTipCoords] = useState(null);
  const rootRef = useRef(null);
  const tipRef = useRef(null);

  const setTooltipOpen = (next) => {
    setOpen(next);
    if (!next) setTipCoords(null);
    onTooltipOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inRoot = rootRef.current?.contains(e.target);
      const inTip = tipRef.current?.contains(e.target);
      if (!inRoot && !inTip) setTooltipOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const roteirizadasHoje = openStats?.roteirizadasHoje ?? 0;
  const amanha = openStats?.amanha ?? 0;
  const futuras = openStats?.futuras ?? 0;

  const concluidasEntries = Object.entries(completedDetails?.concluidas || {}).sort((a, b) => b[1] - a[1]);
  const checkoutEntries = Object.entries(completedDetails?.checkout || {}).sort((a, b) => b[1] - a[1]);
  const showClosedBadge = closedCount > 0;
  const showCompletedInTip = showClosedBadge && (concluidasEntries.length > 0 || checkoutEntries.length > 0);

  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (open) {
      setTooltipOpen(false);
      return;
    }
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) {
      setTipCoords({ left: rect.left, top: rect.bottom + 4 });
    }
    setTooltipOpen(true);
  };

  const tooltip = open && tipCoords ? (
    <div
      ref={tipRef}
      className="tech-col-os-v2-tooltip tech-col-os-v2-tooltip--portal"
      style={{ left: `${tipCoords.left}px`, top: `${tipCoords.top}px` }}
      role="dialog"
      aria-label="Resumo de ordens do técnico"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="tech-col-os-v2-tip-section">
        <div className="tech-col-os-v2-tip-section-title">Abertas</div>
        <div className="tech-col-os-v2-tip-row">
          <span className="tech-col-os-v2-tip-row-label">Roteirizadas para hoje:</span>
          <span className="tech-col-os-v2-tip-row-value">{roteirizadasHoje}</span>
        </div>
        <div className="tech-col-os-v2-tip-row">
          <span className="tech-col-os-v2-tip-row-label">Previstas para amanhã:</span>
          <span className="tech-col-os-v2-tip-row-value">{amanha}</span>
        </div>
        <div className="tech-col-os-v2-tip-row">
          <span className="tech-col-os-v2-tip-row-label">Futuras:</span>
          <span className="tech-col-os-v2-tip-row-value">{futuras}</span>
        </div>
      </div>

      {showCompletedInTip ? (
        <>
          <div className="tech-col-os-v2-tip-divider" aria-hidden />
          <CompletedSection title="Concluídas" entries={concluidasEntries} />
          <CompletedSection title="Check-out" entries={checkoutEntries} />
        </>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={rootRef}
        className={`tech-col-os-v2-badges${open ? ' tech-col-os-v2-badges--open' : ''}`}
        aria-label="Ordens abertas e fechadas"
      >
        <button
          type="button"
          className={`tech-col-os-v2-pill board-clickup-icon-btn-anchor${showClosedBadge ? ' tech-col-os-v2-pill--split' : ''}`}
          onClick={handleToggle}
          onMouseDown={(e) => e.stopPropagation()}
          aria-expanded={open}
          aria-label={
            showClosedBadge
              ? `Ver detalhes do dia: ${openCount} abertas, ${closedCount} concluídas`
              : `Ver detalhes do dia: ${openCount} abertas`
          }
        >
          <span className="tech-col-os-v2-pill-label">
            <span className="tech-col-os-v2-count tech-col-os-v2-count--open">{openCount}</span>
            {showClosedBadge ? (
              <>
                <span className="tech-col-os-v2-count-sep" aria-hidden="true" />
                <span className="tech-col-os-v2-count tech-col-os-v2-count--closed">{closedCount}</span>
              </>
            ) : null}
          </span>
          <span className="board-clickup-tooltip board-clickup-tooltip--bottom" role="tooltip">
            Ver detalhes do dia
          </span>
        </button>
      </div>
      {tooltip ? createPortal(tooltip, document.body) : null}
    </>
  );
}
